import { Component, OnInit } from '@angular/core';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { PageContainerService, TimerService } from '../../services';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { GetSupportTicketCommandRequest, GetSupportTicketCommandResponse, SupportTicketDto, UpdateSupportTicketStatusCommandRequest } from '../../data/CommandServiceDtos.generated';
import { MatDialog } from '@angular/material/dialog';
import { SupportTicketFormComponent } from './support-ticket-form.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { faEye, faMinusCircle } from '@fortawesome/free-solid-svg-icons';

@Component({
  standalone: false,
  selector: 'app-support-ticket',
  templateUrl: './support-ticket.component.html',
  styleUrls: ['./support-ticket.component.scss'],
})
export class SupportTicketComponent extends ControllerForViewBase implements OnInit {
  tickets: SupportTicketDto[] = [];
  faEye = faEye;
  faStatus = faMinusCircle;
  loading = false;
  error: string | null = null;
  SupportTicketType: { [key: number]: string } = {
    0: 'Bug',
    1: 'Suggestion',
    2: 'Feedback',
    3: 'Other'
  };

  SupportTicketStatus: { [key: number]: string } = {
    0: 'Open',
    1: 'InProgress',
    2: 'Resolved',
    3: 'Closed'
  };

  // filters
  SupportTicketStatusArray = [
    { id: 0, name: 'Open' },
    { id: 1, name: 'InProgress' },
    { id: 2, name: 'Resolved' },
    { id: 3, name: 'Closed' }
  ];
  selectedStatus: number | null = null;
  selectedDateRange: string = 'All Time';
  startDate: string | null = null;
  endDate: string | null = null;
  // pagination
  page: number = 1;
  pageSize: number = 10; // default 10 per page
  totalItems: number = 0;
  pager: { pages: number[]; pageSize: number; totalItems: number; currentPage: number; totalPages: number } = { pages: [], pageSize: 10, totalItems: 0, currentPage: 1, totalPages: 1 };

  // form model for create
  //   newTicket: Partial<SupportTicket> = { type: 'Other' };

  constructor(public pcs: PageContainerService, private router: Router, private sanitizer: DomSanitizer, private dialog: MatDialog, public timerService: TimerService) { super(pcs); }

  ngOnInit(): void {
    this.timerService.resetTimer();
    this.loadTickets();
  }

  async loadTickets() {
    var request = new GetSupportTicketCommandRequest();
    request.PageSize = this.pageSize;
    request.CurrentPage = this.page;

    request.Status = this.selectedStatus;
    request.FromDate = this.startDate != null ? new Date(this.startDate) : null;
    request.ToDate = this.endDate != null ? new Date(this.endDate) : null;
    const getCommandResponse: GetSupportTicketCommandResponse = await this.pcs.apiService.GetSupportTicket(request);
    this.tickets = getCommandResponse.SupportTickets || [];
    this.totalItems = getCommandResponse.TotalItems || 0;
    // update pager
    this.pager.pageSize = this.pageSize;
    this.pager.totalItems = this.totalItems;
    this.pager.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    this.pager.currentPage = this.page;
    // build pages array (1..totalPages)
    this.pager.pages = [];
    for (let i = 1; i <= this.pager.totalPages; i++) {
      this.pager.pages.push(i);
    }
  }

  prevPage() {
    if (this.page > 1) {
      this.page--;
      this.loadTickets();
    }
  }

  nextPage() {
    if ((this.page * this.pageSize) < this.totalItems) {
      this.page++;
      this.loadTickets();
    }
  }

  setPage(page: number) {
    if (page < 1) page = 1;
    if (page > this.pager.totalPages) page = this.pager.totalPages;
    if (this.page === page) return;
    this.page = page;
    this.loadTickets();
  }

  applyFilters() {
    this.loadTickets();
  }

  updateDateInputs() {
    // placeholder for any date-range logic (e.g., set start/end for Week/Month)
    if (this.selectedDateRange === 'Week') {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 7);
      this.startDate = start.toISOString();
      this.endDate = now.toISOString();
    } else if (this.selectedDateRange === 'Month') {
      const now = new Date();
      const start = new Date(now);
      start.setMonth(now.getMonth() - 1);
      this.startDate = start.toISOString();
      this.endDate = now.toISOString();
    } else if (this.selectedDateRange === 'All Time') {
      this.startDate = null;
      this.endDate = null;
    }
  }

  getTypeLabelClass(type: number): string {
    switch (type) {
      case 0: return 'label-danger';
      case 1: return 'label-warning';
      case 2: return 'label-success';
      case 3: return 'label-default';
      default: return '';
    }
  }

  getStatusLabelClass(status: number): string {
    switch (status) {
      case 0: return 'label-info';
      case 1: return 'label-warning';
      case 2: return 'label-success';
      case 3: return 'label-default';
      default: return '';
    }
  }

  createTicket() {
    const dialogRef = this.dialog.open(SupportTicketFormComponent, { data: { mode: 'create' }, maxWidth: '90vw', maxHeight: '90vh', width: '50vw', panelClass: 'support-ticket-modal' });
    dialogRef.afterClosed().subscribe((result) => { if (result) { this.loadTickets(); } });
  }

  viewTicket(id: number) {
    const ticket = this.tickets.find(t => t.SupportTicketId === id);
    if (!ticket) return;
    this.dialog.open(SupportTicketFormComponent, { data: { mode: 'view', ticket }, maxWidth: '90vw', maxHeight: '90vh', width: '50vw', panelClass: 'support-ticket-modal' });
  }

  async changeStatus(id: number, status: number) {
    const ticket = this.tickets.find(t => t.SupportTicketId === id);
    if (!ticket)
      return;

    ticket.Status = status;
    // Need to apply confirmation dialog and if user selects okay then proceed with status change
    const confirmation = await this.pcs.confirm(
      'Are you sure you want to change ticket status?',
      'Change status'
    );
    if (confirmation) {
      let commandRequest = new UpdateSupportTicketStatusCommandRequest();
      commandRequest.supportTicket.Title = ticket.Title;
      commandRequest.supportTicket.Description = ticket.Description;
      commandRequest.supportTicket.Type = ticket.Type;
      commandRequest.supportTicket.Status = status;
      commandRequest.supportTicket.SupportTicketId = ticket.SupportTicketId;
      let response = await this.pcs.apiService.UpdateSupportTicketStatus(commandRequest);
      if (response && response.SupportTicket && response.SupportTicket.SupportTicketId) {
        this.pcs.notificationService.success('Support ticket status updated successfully.', '', {
          timeOut: 3000
        });
        this.loadTickets();
      }
    }

  }
}

